import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Loader2, MapPin, X } from 'lucide-react';
import DayView from '../DayView';
import BookingInfoModal from '../BookingInfoModal';
import { CalendarBooking, getBooking, Booking } from '../../lib/bookings';
import { useCalendarDayScheduleData } from '../../hooks/useCalendarDayScheduleData';

/** Fired by the assistant (and anything else) to pop the live team map. */
export const OPEN_TEAM_MAP_EVENT = 'nexus:openTeamMap';

export function openTeamMap(resourceId?: string | null) {
  window.dispatchEvent(new CustomEvent(OPEN_TEAM_MAP_EVENT, { detail: { resourceId: resourceId ?? null } }));
}

interface TeamMapModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Pan to this person when the map opens. */
  focusResourceId?: string | null;
  title?: string;
}

/**
 * The calendar day-view map in a modal: today's jobs, everyone clocked in with
 * a live pin, zoomable. Opened from the Active Workers strip and by the AI.
 */
export default function TeamMapModal({ isOpen, onClose, focusResourceId = null, title }: TeamMapModalProps) {
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const {
    bookings,
    timeOffRequests,
    fieldQuoteEvents,
    resources,
    salesPinsEnabled,
    loading,
    error,
    refresh,
    businessHours,
    businessId,
  } = useCalendarDayScheduleData(today);

  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [focusNonce, setFocusNonce] = useState(0);

  useEffect(() => {
    if (isOpen) setFocusNonce((n) => n + 1);
  }, [isOpen, focusResourceId]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleBookingClick = async ({ event }: { event: CalendarBooking }) => {
    if (
      event.extendedProps?.is_time_off ||
      event.extendedProps?.is_buffer_block ||
      event.extendedProps?.is_task ||
      event.id.startsWith('fieldquote_')
    ) return;
    try {
      setSelectedBooking(await getBooking(event.id));
    } catch (err) {
      console.error('Error opening booking:', err);
    }
  };

  const focusName = focusResourceId
    ? resources.find((r) => r.id === focusResourceId)
    : null;
  const heading = title || (focusName ? `Where ${focusName.first_name || 'they'} ${focusName.last_name || ''}`.trim() + ' is' : 'Live team map');

  return createPortal(
    <div
      className="fixed inset-0 z-[10050] flex items-end justify-center bg-black/50 backdrop-blur-[2px] sm:items-center sm:p-6"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={heading}
    >
      <div
        className="relative flex h-[88dvh] w-full flex-col overflow-hidden rounded-t-2xl bg-white shadow-2xl sm:h-[min(82vh,820px)] sm:max-w-[1100px] sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
        style={{ fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', system-ui, sans-serif" }}
      >
        <div className="flex items-center justify-between gap-3 border-b border-black/[0.06] px-4 py-3">
          <div className="flex min-w-0 items-center gap-2">
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
              <MapPin className="h-4 w-4" strokeWidth={2.25} />
            </span>
            <div className="min-w-0">
              <h2 className="truncate text-[15px] font-semibold text-gray-900" style={{ letterSpacing: '-0.01em' }}>{heading}</h2>
              <p className="text-[12px] text-gray-500">Live. Pins move as phones report in. Zoom out for today’s jobs and the rest of the team.</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-600 transition-colors hover:bg-gray-200"
            aria-label="Close"
          >
            <X className="h-4 w-4" strokeWidth={2.25} />
          </button>
        </div>

        <div className="relative min-h-0 flex-1 bg-[#f8f9fb]">
          {loading ? (
            <div className="absolute inset-0 flex items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          ) : error ? (
            <div className="absolute inset-0 flex items-center justify-center p-4 text-center">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          ) : businessId ? (
            <div className="absolute inset-0">
              <DayView
                embedded
                selectedDate={today}
                resources={resources}
                bookings={bookings}
                timeOffRequests={timeOffRequests}
                fieldQuoteEvents={fieldQuoteEvents}
                onBookingClick={handleBookingClick}
                isLoading={false}
                businessHours={businessHours}
                selectedBusinessId={businessId}
                mapViewEnabled
                salesPinsEnabled={salesPinsEnabled}
                focusResourceId={focusResourceId}
                focusResourceNonce={focusNonce}
              />
            </div>
          ) : null}
        </div>
      </div>

      {selectedBooking && (
        <BookingInfoModal
          isOpen
          onClose={() => setSelectedBooking(null)}
          booking={selectedBooking}
          onUpdate={() => refresh()}
        />
      )}
    </div>,
    document.body,
  );
}

/**
 * Mount once (App shell). Opens the team map when anything dispatches
 * OPEN_TEAM_MAP_EVENT, which is how the in-app assistant shows "where's Yusuf".
 */
export function TeamMapModalHost() {
  const [state, setState] = useState<{ open: boolean; resourceId: string | null }>({ open: false, resourceId: null });

  useEffect(() => {
    const onOpen = (e: Event) => {
      const detail = (e as CustomEvent<{ resourceId?: string | null }>).detail || {};
      setState({ open: true, resourceId: detail.resourceId ?? null });
    };
    window.addEventListener(OPEN_TEAM_MAP_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_TEAM_MAP_EVENT, onOpen);
  }, []);

  if (!state.open) return null;
  return (
    <TeamMapModal
      isOpen
      focusResourceId={state.resourceId}
      onClose={() => setState({ open: false, resourceId: null })}
    />
  );
}
